/**
 * Ad-attribution helpers for feature-flag targeting.
 *
 * The AutoSave onboarding experiment should only be shown to people who arrived
 * from an AutoSave ad. PostHog feature flags target *person* properties, but the
 * landing `utm_campaign` lives on the first pageview event, not on a durable
 * person property (`$initial_utm_campaign` is not populated for this project).
 *
 * So we mirror the existing `platform: "web"` pattern: when a visitor lands from
 * an AutoSave ad, we (1) persist a sticky flag in `localStorage` and (2) push
 * `from_autosave_ad: true` via `setPersonPropertiesForFlags`, so every later
 * flag evaluation (including `/onboarding/begin`) can target it. The localStorage
 * sticky lets us re-apply the property on a hard reload, after the `utm_campaign`
 * has dropped off the URL.
 *
 * AutoSave ad campaigns tag `utm_campaign` with an `autosave-*` concept slug
 * (e.g. `autosave-video`, `autosave-hotel-statics`), set by the meta_ads create
 * scripts. Matching on the `autosave` token keeps this robust as new AutoSave
 * creatives ship, while cleanly excluding the `earn-*` (status-challenge) ads.
 */

const STORAGE_KEY = "gondola.from_autosave_ad";

// utm_campaign for AutoSave ads always contains the `autosave` token; earn /
// status-challenge ads are `earn-*`, so this does not match them.
const AUTOSAVE_CAMPAIGN_PATTERN = /autosave/i;

/** True when the current URL's `utm_campaign` identifies an AutoSave ad. */
export function isFromAutoSaveAd(searchParams: URLSearchParams): boolean {
  const campaign = searchParams.get("utm_campaign") ?? "";
  return AUTOSAVE_CAMPAIGN_PATTERN.test(campaign);
}

/** Sticky record that this visitor arrived from an AutoSave ad. */
export function persistAutoSaveAdAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, "true");
  } catch {
    // localStorage can throw in private mode / sandboxed iframes — the
    // attribution just won't survive a hard reload, which is acceptable.
  }
}

/** Whether a prior pageview recorded an AutoSave-ad arrival for this visitor. */
export function hasAutoSaveAdAttribution(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}
